import { useCallback, useEffect, useRef, useState } from 'react';
import type { LeaderboardResponse } from '@pons/contracts';
import { accountFetch } from '../auth/api';
import {
  leaderboardUrl,
  readLeaderboardSelection,
  type LeaderboardSelection,
} from './model';

export function useLeaderboard(accountId?: string) {
  const [selection, setSelection] = useState(() =>
    readLeaderboardSelection(window.location.search),
  );
  const key = JSON.stringify([accountId, selection]);
  const [state, setState] = useState<{
    key: string;
    data?: LeaderboardResponse;
    error?: string;
  }>({ key });
  const [attempt, setAttempt] = useState(0);
  const requestNumber = useRef(0);
  const navigate = useCallback((next: LeaderboardSelection) => {
    const url = leaderboardUrl(window.location.href, next);
    if (url.href !== window.location.href)
      window.history.pushState(null, '', url);
    setSelection(next);
  }, []);
  useEffect(() => {
    const restore = () =>
      setSelection(readLeaderboardSelection(window.location.search));
    window.addEventListener('popstate', restore);
    return () => window.removeEventListener('popstate', restore);
  }, []);
  useEffect(() => {
    let stopped = false;
    let controller: AbortController | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      controller?.abort();
      clearTimeout(timeout);
      const current = ++requestNumber.current;
      controller = new AbortController();
      const signal = controller.signal;
      timeout = setTimeout(() => controller?.abort('timeout'), 15000);
      const query = new URLSearchParams({
        period: selection.period,
        page: String(selection.page),
      });
      if (selection.period === 'week' && selection.week)
        query.set('periodId', selection.week);
      try {
        const response = await accountFetch(
          accountId,
          `/api/leaderboard?${query}`,
          { signal, cache: 'no-store' },
        );
        const body = await response.json();
        if (!response.ok)
          throw new Error(
            body.error || 'Leaderboard is unavailable. Please try again.',
          );
        if (stopped || current !== requestNumber.current) return;
        setState({ key, data: body as LeaderboardResponse });
      } catch (cause) {
        if (stopped || current !== requestNumber.current) return;
        const error = signal.aborted
          ? 'The leaderboard took too long to load. Please try again.'
          : cause instanceof Error
            ? cause.message
            : 'Leaderboard is unavailable. Please try again.';
        setState((previous) => ({
          key,
          data: previous.key === key ? previous.data : undefined,
          error,
        }));
      } finally {
        if (current === requestNumber.current) clearTimeout(timeout);
      }
    };
    void load();
    const refresh = () => {
      if (!document.hidden) void load();
    };
    const interval = setInterval(refresh, 15000);
    window.addEventListener('pons:points-changed', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      stopped = true;
      controller?.abort();
      clearTimeout(timeout);
      clearInterval(interval);
      window.removeEventListener('pons:points-changed', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [
    accountId,
    selection.period,
    selection.week,
    selection.page,
    key,
    attempt,
  ]);
  const current = state.key === key ? state : undefined;
  return {
    selection,
    navigate,
    data: current?.data,
    error: current?.error,
    loading: !current?.data && !current?.error,
    retry: () => setAttempt((value) => value + 1),
  };
}
